/*
  THAANI UI: kleine globale Effekte.
  - .ui-reveal blendet beim Einscrollen ein.
  - [data-ui-parallax] verschiebt schwebende Elemente leicht gegen die
    Scrollrichtung (Faktor im Attribut, Standard 0.06).
  Beides entfaellt bei prefers-reduced-motion.
*/
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const reveal = () => {
    const items = document.querySelectorAll('.ui-reveal:not(.is-in)');
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => io.observe(el));

    // Spruenge (Anker, schnelles Scrollen) ueberspringen Elemente, ohne dass sie
    // je den Viewport schneiden. Alles oberhalb der Unterkante daher direkt zeigen.
    let frame = null;
    const sweep = () => {
      frame = null;
      const limit = window.innerHeight;
      document.querySelectorAll('.ui-reveal:not(.is-in)').forEach((el) => {
        if (el.getBoundingClientRect().top < limit) {
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    };
    window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(sweep); }, { passive: true });
  };

  const parallax = () => {
    if (reduce) return;
    const items = [...document.querySelectorAll('[data-ui-parallax]')];
    if (!items.length) return;
    let frame = null;
    const update = () => {
      frame = null;
      const vh = window.innerHeight;
      items.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const factor = Number(el.dataset.uiParallax) || 0.06;
        const offset = (rect.top + rect.height / 2 - vh / 2) * -factor;
        el.style.setProperty('--py', `${offset.toFixed(1)}px`);
      });
    };
    window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(update); }, { passive: true });
    update();
  };

  // Splash-Videos wie die alte Startseite: Explosion einmal abspielen, ab
  // 35 % der Laufzeit tritt das Video weich gezeichnet und leiser zurueck
  // (Klasse is-fokus) und bleibt so stehen. Kein Loop. Im Hero startet der
  // Produktwechsel das Video (sections/thaani-ui-hero.liquid), hier nur
  // die uebrigen Splashes beim ersten Sichtbarwerden.
  const FOKUS_AB = 0.35;
  const splash = () => {
    const videos = [...document.querySelectorAll('.ui-splash__video')];
    videos.forEach((v) => {
      // Seitenkopf mit Schleier: Video laeuft in Schleife, ohne Weichzeichnen.
      if (v.closest('.ui-kopf__video--schleier')) return;
      v.loop = false;
      if (v.dataset.uiSplash) return;
      v.dataset.uiSplash = '1';
      const box = v.closest('.ui-splash');
      v.addEventListener('timeupdate', () => {
        let dauer = v.duration;
        if (!Number.isFinite(dauer) && v.seekable && v.seekable.length) dauer = v.seekable.end(v.seekable.length - 1);
        v.style.setProperty('--ui-splash-fade', Math.min(1, v.currentTime / 0.5).toFixed(2));
        if (Number.isFinite(dauer) && dauer > 0 && box) box.classList.toggle('is-fokus', v.currentTime / dauer >= FOKUS_AB);
      });
      v.addEventListener('ended', () => box && box.classList.add('is-fokus'));
    });
    if (reduce) { videos.forEach((v) => v.pause()); return; }
    if (!('IntersectionObserver' in window)) return;
    // Erst nach dem load-Ereignis laden: das Video ist kaum sichtbar und soll
    // Bilder und Schrift nicht ausbremsen. Wer es vorher mit dem Knopf im
    // Seitenkopf angehalten hat, dem startet es auch dann nicht neu.
    const starten = (v) => {
      if (v.dataset.uiAngehalten) return;
      v.preload = 'auto';
      v.play().catch(() => {});
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const v = e.target;
        io.unobserve(v);
        if (document.readyState === 'complete') starten(v);
        else window.addEventListener('load', () => starten(v), { once: true });
      });
    }, { threshold: 0.25 });
    videos.filter((v) => !v.closest('.ui-hero__slide')).forEach((v) => io.observe(v));
  };

  // Bogen unter einer Buehne: Farbe der folgenden Section uebernehmen, damit
  // kein fremder Streifen entsteht (Chris, 16.10., B2B).
  // Liegt in derselben Section noch eine Flaeche unter der Buehne (Collection:
  // Raster direkt unter dem Kopf), zaehlt diese zuerst (Chris, 20.10.).
  // Fixierte Elemente wie die Kaufleiste liegen nicht unter der Buehne.
  const imFluss = (el) => {
    if (!el.offsetHeight) return false;
    const cs = getComputedStyle(el);
    return cs.display !== 'none' && cs.position !== 'fixed' && cs.position !== 'absolute';
  };
  const curves = () => {
    document.querySelectorAll('.ui-curve').forEach((curve) => {
      const section = curve.closest('.shopify-section');
      let el = curve.closest('.ui-stage') || curve.parentElement;
      let next = null;
      while (el && !next) {
        next = el.nextElementSibling;
        while (next && !imFluss(next)) next = next.nextElementSibling;
        if (el === section) break;
        el = el.parentElement;
      }
      let farbe = null;
      // Nur Flaechen ueber die ganze Breite, die oben an der Folgeflaeche
      // beginnen. Karten weiter unten (Sortiment) zaehlen nicht (Chris, 20.10.).
      const oben = next ? next.getBoundingClientRect().top : 0;
      const breite = curve.offsetWidth * 0.9;
      const kandidaten = next ? [next, ...next.querySelectorAll('*')].slice(0, 60) : [];
      for (const el of kandidaten) {
        const bg = getComputedStyle(el).backgroundColor;
        if (!bg || bg === 'transparent' || /rgba\(\d+, \d+, \d+, 0\)/.test(bg)) continue;
        const r = el.getBoundingClientRect();
        if (r.width >= breite && r.top - oben <= 8) { farbe = bg; break; }
      }
      if (!farbe) farbe = getComputedStyle(document.body).backgroundColor;
      curve.style.color = farbe;
    });
  };

  // Seitenkopf mit mehreren Hintergrundbildern: alle data-dauer Sekunden
  // sanft weiterblenden (CSS-Transition auf is-aktiv). Bei reduzierter
  // Bewegung bleibt das erste Bild stehen.
  const wechsel = () => {
    document.querySelectorAll('.ui-kopf__video--wechsel').forEach((box) => {
      if (box.dataset.uiWechsel) return;
      box.dataset.uiWechsel = '1';
      const bilder = [...box.querySelectorAll('.ui-kopf__bild')];
      if (bilder.length < 2 || reduce) return;
      const ms = (parseFloat(box.dataset.dauer) || 7) * 1000;
      let i = 0;
      const timer = setInterval(() => {
        if (!box.isConnected) { clearInterval(timer); return; }
        if (document.hidden) return;
        bilder[i].classList.remove('is-aktiv');
        i = (i + 1) % bilder.length;
        bilder[i].classList.add('is-aktiv');
      }, ms);
    });
  };

  // Hintergrundvideo im Seitenkopf anhalten und weiterspielen (WCAG 2.2.2).
  // aria-pressed folgt dem Video, auch wenn der Browser Autoplay blockiert
  // oder das einmal laufende Video endet. Bei reduzierter Bewegung sind
  // Video und Knopf per CSS ausgeblendet.
  const kopfPause = () => {
    document.querySelectorAll('[data-ui-kopf-pause]').forEach((knopf) => {
      if (knopf.dataset.uiKopfPause) return;
      knopf.dataset.uiKopfPause = '1';
      const v = knopf.parentElement.querySelector('.ui-kopf__video video');
      if (!v) { knopf.hidden = true; return; }
      const stand = () => knopf.setAttribute('aria-pressed', String(v.paused));
      v.addEventListener('play', stand);
      v.addEventListener('pause', stand);
      knopf.addEventListener('click', () => {
        if (v.paused) {
          delete v.dataset.uiAngehalten;
          v.play().catch(() => {});
        } else {
          v.dataset.uiAngehalten = '1';
          v.pause();
        }
      });
    });
  };

  const init = () => { reveal(); parallax(); splash(); curves(); wechsel(); kopfPause(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
